// 位圖數字 — 以 Unity 位圖字型（tx_win／tx_Multiplier／tx_freeSpin）逐字排版一串數字
// （對照 Unity 的 LocalizeNumberText/TMP 位圖字）。
//
// 用於大獎/免費次數/倍率等需要「數字美術」的顯示；相較一般 Text，數字滾動動畫期間不需每幀重繪文字紋理。
// 未知字元（如貨幣符號、空白）以固定間隙略過。整串以水平置中、垂直置中對齊本地原點 (0,0)。

import { Container, Sprite } from 'pixi.js'
import { getNumberGlyph, isNumberFontLoaded, numberFontHeight, type NumberFontName } from '../assets/NumberFont'

export class BitmapNumber extends Container {
  /** 目標顯示高度（px）；glyph 依此等比縮放。 */
  private targetHeight = 48
  /** 字元間距（相對目標高度的比例）；可為負值＝字距收緊（對應 TMP characterSpacing）。 */
  private letterSpacingRatio = 0.02
  private currentText = ''
  /** 目前這串字的實際寬度（px，不含頭尾間距）；供呼叫端排底板等版面用。 */
  private measuredWidth = 0

  constructor(private readonly font: NumberFontName = 'win') {
    super()
  }

  /** 設定顯示高度（供 resize 依版面調整），並以現有內容重排。 */
  setHeight(height: number): void {
    if (height === this.targetHeight) return
    this.targetHeight = height
    this.render(this.currentText)
  }

  /**
   * 設定字距比例（相對目標高度）。負值＝收緊，對應 Unity TMP 的 `m_characterSpacing`
   * （TMP 換算：每字間距 = characterSpacing × 0.01 × fontSize）。
   */
  setLetterSpacing(ratio: number): void {
    if (ratio === this.letterSpacingRatio) return
    this.letterSpacingRatio = ratio
    this.render(this.currentText)
  }

  /** 這串字排完後的實際寬度（px）。 */
  get contentWidth(): number {
    return this.measuredWidth
  }

  /** 設定要顯示的數字字串（如 "1,234.56"、"X16"）。未知字元以間隙略過。 */
  setText(text: string): void {
    if (text === this.currentText) return
    this.render(text)
  }

  private render(text: string): void {
    this.currentText = text
    for (const child of this.removeChildren()) child.destroy()

    // 字型未載入時不繪製（呼叫端應已確保載入；防禦性回退避免除以 0）。
    this.measuredWidth = 0
    if (!isNumberFontLoaded(this.font)) return
    const scale = this.targetHeight / numberFontHeight(this.font)
    const spacing = this.targetHeight * this.letterSpacingRatio
    const gap = this.targetHeight * 0.32 // 未知字元（空白/貨幣符號）的間隙

    // 間距加在「字與字之間」（首字前不加、末字後不加），故 x 收尾即為整串實際寬度。
    let x = 0
    let isFirst = true
    for (const char of text) {
      if (!isFirst) x += spacing
      isFirst = false
      const glyph = getNumberGlyph(char, this.font)
      if (glyph === null) {
        x += gap
        continue
      }
      const sprite = new Sprite(glyph.texture)
      sprite.anchor.set(0, 0.5)
      sprite.scale.set(scale)
      sprite.position.set(x, 0)
      this.addChild(sprite)
      x += glyph.width * scale
    }

    // 以整串寬度置中於本地原點（垂直已由 anchor.y=0.5 置中）。
    this.measuredWidth = x
    this.pivot.set(x / 2, 0)
  }
}
